import { useState, useEffect, useCallback } from "react";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged, sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc, getDoc, setDoc, updateDoc, collection,
  addDoc, onSnapshot, query, where, deleteDoc,
} from "firebase/firestore";
import { auth, db } from "./firebase";

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  purple:"#7C3AED", purpleLight:"#EDE9FE", purpleMid:"#A78BFA",
  pink:  "#EC4899", pinkLight:  "#FCE7F3", pinkMid:  "#F9A8D4",
  orange:"#F97316", orangeLight:"#FFF7ED", orangeMid:"#FED7AA",
  teal:  "#0D9488", tealLight:  "#CCFBF1", tealMid:  "#5EEAD4",
  yellow:"#EAB308", yellowLight:"#FEF9C3", yellowMid:"#FDE047",
  green: "#16A34A", greenLight: "#DCFCE7", greenMid: "#86EFAC",
  blue:  "#2563EB", blueLight:  "#DBEAFE", blueMid:  "#93C5FD",
  red:   "#DC2626", redLight:   "#FEE2E2", redMid:   "#FCA5A5",
  white: "#FFFFFF", offWhite:   "#F8F7FF",
  gray50:"#F9FAFB", gray100:"#F3F4F6", gray200:"#E5E7EB",
  gray400:"#9CA3AF", gray600:"#4B5563", gray800:"#1F2937",
};

const CHILD_COLORS = [
  { bg:C.purple, light:C.purpleLight, mid:C.purpleMid },
  { bg:C.pink,   light:C.pinkLight,   mid:C.pinkMid   },
  { bg:C.teal,   light:C.tealLight,   mid:C.tealMid   },
  { bg:C.orange, light:C.orangeLight, mid:C.orangeMid },
];

const FREQ_CONFIG = {
  daily:  { label:"Daily",   color:C.purple, light:C.purpleLight, icon:"☀️" },
  weekly: { label:"Weekly",  color:C.teal,   light:C.tealLight,   icon:"📅" },
  monthly:{ label:"Monthly", color:C.orange, light:C.orangeLight, icon:"🗓️" },
};

const DAY_SHORT   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ─── Date Helpers ─────────────────────────────────────────────────────────────
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};
const dateToStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

function getWeekDates() {
  const today = new Date();
  const day = today.getDay();
  const mon = new Date(today);
  mon.setDate(today.getDate() - day + (day === 0 ? -6 : 1));
  return Array.from({ length:7 }, (_, i) => {
    const d = new Date(mon); d.setDate(mon.getDate()+i); return dateToStr(d);
  });
}

function getMonthDates() {
  const today = new Date();
  const year = today.getFullYear(), month = today.getMonth();
  const days = new Date(year, month+1, 0).getDate();
  return Array.from({ length:days }, (_, i) => dateToStr(new Date(year, month, i+1)));
}

function choreAppliesToDate(chore, dateStr) {
  if (!chore.startDate) return false;
  if (dateStr < chore.startDate) return false;
  if (chore.endDate && dateStr > chore.endDate) return false;
  const d = new Date(dateStr + "T00:00:00");
  if (chore.freq === "daily") return true;
  if (chore.freq === "weekly") return Array.isArray(chore.weekDays) && chore.weekDays.includes(d.getDay());
  if (chore.freq === "monthly") return chore.monthDay === d.getDate();
  return false;
}

function ordinal(n) {
  const s = ["th","st","nd","rd"], v = n % 100;
  return s[(v-20)%10] || s[v] || s[0];
}

// ─── Misc Helpers ─────────────────────────────────────────────────────────────
const fmt$ = (n) => "$" + Number(n||0).toFixed(2);
const initials = (name) => (name||"?").trim().split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);

function getChildBalance(transactions, childId) {
  let chore = 0, cash = 0;
  for (const tx of (transactions||[])) {
    if (tx.childId !== childId) continue;
    if (tx.type==="chore_credit") chore += tx.amount;
    else if (tx.type==="chore_spend") chore -= tx.amount;
    else if (tx.type==="cash_gift") cash += tx.amount;
    else if (tx.type==="cash_spend") cash -= tx.amount;
  }
  return { chore: Math.max(0,chore), cash: Math.max(0,cash) };
}

// ─── UI Components ────────────────────────────────────────────────────────────
function Avatar({ name, color, size=40 }) {
  return <div style={{ width:size, height:size, borderRadius:"50%", background:color?.bg||C.purple, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.33, fontWeight:700, flexShrink:0 }}>{initials(name)}</div>;
}

function Btn({ children, onClick, variant="default", size="md", style:s={}, disabled, type="button" }) {
  const base = { border:"none", borderRadius:12, cursor:disabled?"not-allowed":"pointer", fontWeight:600, transition:"all 0.15s", display:"inline-flex", alignItems:"center", gap:6, opacity:disabled?0.5:1, fontFamily:"inherit" };
  const sizes = { sm:{padding:"6px 12px",fontSize:12}, md:{padding:"10px 18px",fontSize:14}, lg:{padding:"14px 28px",fontSize:16} };
  const variants = {
    default:{ background:C.gray100, color:C.gray800 },
    primary:{ background:C.purple,  color:"#fff"    },
    success:{ background:C.green,   color:"#fff"    },
    danger: { background:C.redLight,color:C.red     },
    ghost:  { background:"transparent", color:C.gray600 },
    teal:   { background:C.teal,    color:"#fff"    },
    orange: { background:C.orange,  color:"#fff"    },
    pink:   { background:C.pink,    color:"#fff"    },
  };
  return <button type={type} disabled={disabled} onClick={onClick} style={{...base,...sizes[size],...variants[variant],...s}}>{children}</button>;
}

function Card({ children, style:s={} }) {
  return <div style={{ background:C.white, borderRadius:20, padding:"20px 24px", marginBottom:16, boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1px solid ${C.gray200}`, ...s }}>{children}</div>;
}

function Input({ label, error, ...props }) {
  return (
    <div style={{ marginBottom:12 }}>
      {label && <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.gray600, marginBottom:4 }}>{label}</label>}
      <input {...props} style={{ width:"100%", padding:"10px 14px", borderRadius:10, border:`1.5px solid ${error?C.red:C.gray200}`, fontSize:14, fontFamily:"inherit", background:C.gray50, color:C.gray800, boxSizing:"border-box", outline:"none", ...props.style }} />
      {error && <div style={{ fontSize:12, color:C.red, marginTop:3 }}>{error}</div>}
    </div>
  );
}

function Select({ label, children, ...props }) {
  return (
    <div style={{ marginBottom:12 }}>
      {label && <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.gray600, marginBottom:4 }}>{label}</label>}
      <select {...props} style={{ width:"100%", padding:"10px 14px", borderRadius:10, border:`1.5px solid ${C.gray200}`, fontSize:14, fontFamily:"inherit", background:C.gray50, color:C.gray800, boxSizing:"border-box", outline:"none", ...props.style }}>{children}</select>
    </div>
  );
}

function Checkbox({ label, checked, onChange, note }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:12, cursor:"pointer" }} onClick={()=>onChange(!checked)}>
      <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${checked?C.purple:C.gray300}`, background:checked?C.purple:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1, transition:"all 0.15s" }}>
        {checked && <span style={{ color:"#fff", fontSize:13, fontWeight:700 }}>✓</span>}
      </div>
      <div>
        <div style={{ fontSize:14, fontWeight:600, color:C.gray800 }}>{label}</div>
        {note && <div style={{ fontSize:12, color:C.gray400, marginTop:2 }}>{note}</div>}
      </div>
    </div>
  );
}

function Toast({ message, type="info", onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return <div style={{ position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)", background:type==="error"?C.red:C.gray800, color:"#fff", padding:"12px 24px", borderRadius:16, fontSize:14, fontWeight:600, zIndex:2000, boxShadow:"0 8px 24px rgba(0,0,0,0.2)", whiteSpace:"nowrap", maxWidth:"90vw" }}>{message}</div>;
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:500, padding:20 }}>
      <div style={{ background:C.white, borderRadius:24, padding:28, width:"100%", maxWidth:wide?580:440, boxShadow:"0 20px 60px rgba(0,0,0,0.2)", maxHeight:"90vh", overflowY:"auto" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div style={{ fontSize:18, fontWeight:700, color:C.gray800 }}>{title}</div>
          <button onClick={onClose} style={{ border:"none", background:C.gray100, borderRadius:8, width:32, height:32, cursor:"pointer", fontSize:16, color:C.gray600 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Spinner({ text="Loading…" }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"60vh", gap:16 }}>
      <div style={{ width:44, height:44, borderRadius:"50%", border:`4px solid ${C.purpleLight}`, borderTopColor:C.purple, animation:"spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ color:C.gray400, fontWeight:600 }}>{text}</div>
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  return (
    <div style={{ display:"flex", gap:4, background:C.gray100, borderRadius:14, padding:4, width:"fit-content" }}>
      {["daily","weekly","monthly"].map(v => (
        <button key={v} onClick={()=>onChange(v)} style={{ padding:"7px 16px", borderRadius:10, border:"none", cursor:"pointer", fontSize:13, fontWeight:600, transition:"all 0.15s", background:view===v?C.purple:"transparent", color:view===v?"#fff":C.gray600 }}>
          {FREQ_CONFIG[v].icon} {FREQ_CONFIG[v].label}
        </button>
      ))}
    </div>
  );
}

function ChildSelector({ members, activeChild, onSelect, highlightCounts }) {
  return (
    <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
      {(members||[]).filter(m=>m.role==="child").map((ch,i) => {
        const cc = CHILD_COLORS[ch.colorIdx??i%CHILD_COLORS.length];
        const count = highlightCounts?.[ch.id]||0;
        return (
          <button key={ch.id} onClick={()=>onSelect(ch.id)} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 16px", borderRadius:20, border:`2px solid ${activeChild===ch.id?cc.bg:C.gray200}`, background:activeChild===ch.id?cc.light:C.white, cursor:"pointer", fontWeight:700, fontSize:13, color:activeChild===ch.id?cc.bg:C.gray600 }}>
            <Avatar name={ch.name} color={cc} size={24} />
            {ch.name}
            {!ch.hasDevice && <span style={{ fontSize:10, background:C.orangeLight, color:C.orange, padding:"1px 6px", borderRadius:8, fontWeight:700 }}>no device</span>}
            {count>0 && <span style={{ background:C.orange, color:"#fff", borderRadius:10, padding:"1px 7px", fontSize:11 }}>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ─── Schedule Picker ──────────────────────────────────────────────────────────
function SchedulePicker({ freq, value, onChange }) {
  const endField = (
    <Input label="End date (optional — leave blank for ongoing)" type="date" value={value.endDate||""} min={value.startDate||todayStr()} onChange={e=>onChange({...value, endDate:e.target.value||null})} />
  );
  if (freq==="daily") return (
    <>
      <Input label="Start date" type="date" value={value.startDate||todayStr()} min={todayStr()} onChange={e=>onChange({...value, startDate:e.target.value})} />
      {endField}
    </>
  );
  if (freq==="weekly") {
    const days = value.weekDays||[];
    return (
      <div>
        <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.gray600, marginBottom:8 }}>Which day(s) of the week?</label>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
          {DAY_SHORT.map((d,i) => {
            const sel = days.includes(i);
            return <button key={i} type="button" onClick={()=>{ const next=sel?days.filter(x=>x!==i):[...days,i]; onChange({...value,weekDays:next}); }} style={{ padding:"7px 12px", borderRadius:10, border:`2px solid ${sel?C.teal:C.gray200}`, background:sel?C.tealLight:C.gray50, color:sel?C.teal:C.gray600, fontWeight:700, fontSize:13, cursor:"pointer" }}>{d}</button>;
          })}
        </div>
        <Input label="Start date" type="date" value={value.startDate||todayStr()} min={todayStr()} onChange={e=>onChange({...value, startDate:e.target.value})} />
        {endField}
      </div>
    );
  }
  if (freq==="monthly") {
    return (
      <div>
        <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.gray600, marginBottom:8 }}>Which day of the month?</label>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:10 }}>
          {Array.from({length:31},(_,i)=>i+1).map(d => {
            const sel = value.monthDay===d;
            return <button key={d} type="button" onClick={()=>onChange({...value,monthDay:d})} style={{ width:38, height:38, borderRadius:10, border:`2px solid ${sel?C.orange:C.gray200}`, background:sel?C.orangeLight:C.gray50, color:sel?C.orange:C.gray600, fontWeight:700, fontSize:13, cursor:"pointer" }}>{d}</button>;
          })}
        </div>
        <Input label="Start date" type="date" value={value.startDate||todayStr()} min={todayStr()} onChange={e=>onChange({...value, startDate:e.target.value})} />
        {endField}
      </div>
    );
  }
  return null;
}

// ─── Calendar Views ───────────────────────────────────────────────────────────
function WeeklyCalendar({ getChoresForDate, today, accentColor, onMarkDone, canMark }) {
  const weekDates = getWeekDates();
  return (
    <div style={{ overflowX:"auto", paddingBottom:8 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7, minmax(110px,1fr))", gap:8, minWidth:700 }}>
        {weekDates.map(date => {
          const items = getChoresForDate(date);
          const dt = new Date(date+"T00:00:00");
          const isToday = date===today;
          const isPast = date<today;
          const isFuture = date>today;
          const earned = items.filter(i=>i.assignment?.status==="verified").reduce((s,i)=>s+i.chore.value,0);
          return (
            <div key={date} style={{ borderRadius:16, border:`2px solid ${isToday?accentColor:C.gray200}`, background:isToday?`${accentColor}15`:C.white, overflow:"hidden" }}>
              <div style={{ padding:"10px 10px 8px", background:isToday?accentColor:C.gray100, textAlign:"center" }}>
                <div style={{ fontSize:12, fontWeight:800, color:isToday?"#fff":C.gray600 }}>{DAY_SHORT[dt.getDay()]}</div>
                <div style={{ fontSize:18, fontWeight:800, color:isToday?"#fff":C.gray800, lineHeight:1.2 }}>{dt.getDate()}</div>
                {isToday && <div style={{ fontSize:10, color:"rgba(255,255,255,0.85)", fontWeight:700, marginTop:2 }}>TODAY</div>}
                {earned>0 && <div style={{ fontSize:10, color:isToday?"rgba(255,255,255,0.9)":C.green, fontWeight:700, marginTop:2 }}>+{fmt$(earned)}</div>}
              </div>
              <div style={{ padding:"8px 6px", display:"flex", flexDirection:"column", gap:5 }}>
                {items.length===0
                  ? <div style={{ textAlign:"center", color:C.gray400, fontSize:11, padding:"8px 0" }}>—</div>
                  : items.map(({ chore:ch, assignment:a }) => {
                    const done = a?.status==="done"||a?.status==="verified";
                    const verified = a?.status==="verified";
                    const canClick = canMark ? (isToday && !done) : false;
                    return (
                      <div key={ch.id} style={{ background:verified?C.greenLight:done?C.yellowLight:isPast?C.gray100:C.white, borderRadius:10, padding:"7px 8px", border:`1.5px solid ${verified?C.greenMid:done?C.yellowMid:C.gray200}`, opacity:isFuture?0.7:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                          <button onClick={()=>canClick&&onMarkDone(ch,a,date)} style={{ width:22, height:22, borderRadius:"50%", flexShrink:0, border:`2px solid ${verified?C.green:done?C.yellow:canClick?accentColor:C.gray300}`, background:verified?C.green:done?C.yellow:"transparent", cursor:canClick?"pointer":"default", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11 }}>
                            {verified?"✓":done?"⏳":(isPast||isFuture)&&!done?"🔒":""}
                          </button>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:C.gray800, textDecoration:done?"line-through":"none", opacity:done?0.6:1, lineHeight:1.3, wordBreak:"break-word" }}>{ch.name}</div>
                            <div style={{ fontSize:10, fontWeight:700, color:verified?C.green:C.gray400, marginTop:1 }}>{fmt$(ch.value)}</div>
                          </div>
                        </div>
                        {isToday&&verified&&<div style={{ fontSize:10, color:C.green, fontWeight:700, marginTop:3 }}>✅ Verified!</div>}
                        {isToday&&done&&!verified&&<div style={{ fontSize:10, color:C.orange, fontWeight:600, marginTop:3 }}>⏳ Pending</div>}
                        {isToday&&!done&&canMark&&<div style={{ fontSize:10, color:C.gray400, marginTop:3 }}>Tap ○ when done</div>}
                      </div>
                    );
                  })
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthlyCalendar({ getChoresForDate, today, accentColor }) {
  const monthDates = getMonthDates();
  const firstDay = new Date(monthDates[0]+"T00:00:00").getDay();
  const dt0 = new Date(monthDates[0]+"T00:00:00");
  return (
    <div style={{ width:"100%", overflowX:"hidden" }}>
      <div style={{ fontWeight:700, fontSize:15, color:C.gray800, marginBottom:12, textAlign:"center" }}>
        {MONTH_NAMES[dt0.getMonth()]} {dt0.getFullYear()}
      </div>
      {/* Day headers */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, marginBottom:2 }}>
        {DAY_SHORT.map(d => <div key={d} style={{ textAlign:"center", fontSize:10, fontWeight:700, color:C.gray400, padding:"4px 0" }}>{d}</div>)}
      </div>
      {/* Calendar grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
        {Array.from({length:firstDay},(_,i)=><div key={`empty-${i}`}/>)}
        {monthDates.map(date => {
          const items = getChoresForDate(date);
          const dt = new Date(date+"T00:00:00");
          const isToday = date===today;
          const total = items.length;
          return (
            <div key={date} style={{ borderRadius:8, border:`2px solid ${isToday?accentColor:C.gray200}`, background:isToday?`${accentColor}18`:C.white, padding:"4px 3px", minHeight:58, overflow:"hidden", minWidth:0, boxSizing:"border-box" }}>
              <div style={{ fontSize:11, fontWeight:800, color:isToday?accentColor:C.gray600, textAlign:"center", marginBottom:3 }}>{dt.getDate()}</div>
              {total>0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                  {items.slice(0,2).map(({chore:ch, assignment:a})=>{
                    const verified = a?.status==="verified";
                    const done = a?.status==="done";
                    return (
                      <div key={ch.id} style={{ fontSize:8, fontWeight:600, padding:"2px 3px", borderRadius:3, background:verified?C.greenLight:done?C.yellowLight:C.purpleLight, color:verified?C.green:done?C.orange:C.purple, lineHeight:1.2, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis", maxWidth:"100%" }}>
                        {ch.name}
                      </div>
                    );
                  })}
                  {total>2 && <div style={{ fontSize:8, color:C.gray400, textAlign:"center", fontWeight:600 }}>+{total-2}</div>}
                </div>
              )}
              {total===0 && <div style={{ textAlign:"center", color:C.gray200, fontSize:12 }}>·</div>}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div style={{ display:"flex", gap:16, marginTop:14, justifyContent:"center", flexWrap:"wrap" }}>
        {[{bg:C.purpleLight,color:C.purple,label:"Assigned"},{bg:C.yellowLight,color:C.orange,label:"Pending verify"},{bg:C.greenLight,color:C.green,label:"Verified"}].map(l=>(
          <div key={l.label} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:l.color, fontWeight:600 }}>
            <div style={{ width:10, height:10, borderRadius:3, background:l.bg, border:`1px solid ${l.color}` }}/>
            {l.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Auth Screen ──────────────────────────────────────────────────────────────
function AuthScreen() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState(""); const [name, setName] = useState("");
  const [familyName, setFamilyName] = useState(""); const [loading, setLoading] = useState(false);
  const [error, setError] = useState(""); const [resetSent, setResetSent] = useState(false);

  function resetFields() { setEmail(""); setPassword(""); setNewPassword(""); setName(""); setFamilyName(""); setError(""); }

  function friendlyError(code) {
    return { "auth/user-not-found":"No account found with that email.", "auth/wrong-password":"Incorrect password.", "auth/email-already-in-use":"That email is already registered.", "auth/weak-password":"Password must be at least 6 characters.", "auth/invalid-email":"Please enter a valid email address.", "auth/invalid-credential":"Incorrect email or password." }[code] || "Something went wrong. Please try again.";
  }

  async function handleLogin(e) {
    e.preventDefault(); setError(""); setLoading(true);
    try { await signInWithEmailAndPassword(auth, email, password); }
    catch(err) { setError(friendlyError(err.code)); }
    setLoading(false);
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (!name.trim()||!familyName.trim()) { setError("Please fill in all fields."); return; }
    setError(""); setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const familyRef = await addDoc(collection(db,"families"), { name:familyName.trim(), createdAt:new Date().toISOString(), createdBy:cred.user.uid });
      await setDoc(doc(db,"users",cred.user.uid), { name:name.trim(), email:email.trim().toLowerCase(), role:"parent", familyId:familyRef.id, colorIdx:0, createdAt:new Date().toISOString() });
    } catch(err) { setError(friendlyError(err.code)); }
    setLoading(false);
  }

  async function handleReset(e) {
    e.preventDefault(); setError(""); setLoading(true);
    try { await sendPasswordResetEmail(auth, email); setResetSent(true); }
    catch(err) { setError(friendlyError(err.code)); }
    setLoading(false);
  }

  async function handleJoin(e) {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const q = query(collection(db,"pendingMembers"), where("email","==",email.trim().toLowerCase()));
      let snap = null;
      await new Promise((resolve,reject) => { const u = onSnapshot(q, s=>{ snap=s; u(); resolve(); }, reject); setTimeout(()=>{ u(); resolve(); },8000); });
      if (!snap||snap.empty) { setError("No invite found for that email."); setLoading(false); return; }
      const inviteDoc = snap.docs[0]; const invite = inviteDoc.data();
      if (invite.tempPassword!==password) { setError("Incorrect temporary password."); setLoading(false); return; }
      const cred = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), newPassword);
      await setDoc(doc(db,"users",cred.user.uid), { name:invite.name, email:email.trim().toLowerCase(), role:invite.role, familyId:invite.familyId, colorIdx:invite.colorIdx, hasDevice:true, createdAt:new Date().toISOString() });
      await deleteDoc(doc(db,"pendingMembers",inviteDoc.id));
    } catch(err) { setError(friendlyError(err.code)); }
    setLoading(false);
  }

  const wrap = (children) => (
    <div style={{ minHeight:"100vh", background:`linear-gradient(135deg, ${C.purpleLight} 0%, ${C.pinkLight} 50%, ${C.blueLight} 100%)`, display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:"system-ui, sans-serif" }}>
      <div style={{ width:"100%", maxWidth:440 }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:52, marginBottom:6 }}>🏠✨</div>
          <h1 style={{ fontSize:30, fontWeight:800, color:C.purple, margin:0, letterSpacing:-1 }}>ChoreChart</h1>
          <p style={{ color:C.gray600, marginTop:6, fontSize:14, margin:"6px 0 0" }}>Chores. Rewards. Family wins.</p>
        </div>
        <Card>{children}</Card>
      </div>
    </div>
  );

  if (mode==="reset") return wrap(resetSent
    ? <div style={{ textAlign:"center" }}><div style={{ fontSize:40, marginBottom:12 }}>📧</div><div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>Check your inbox</div><div style={{ color:C.gray600, fontSize:14, marginBottom:20 }}>We sent a reset link to {email}</div><Btn variant="primary" onClick={()=>{ setMode("login"); setResetSent(false); }} style={{ width:"100%", justifyContent:"center" }}>Back to log in</Btn></div>
    : <><h2 style={{ margin:"0 0 6px", fontSize:19, fontWeight:700 }}>Reset your password</h2><p style={{ margin:"0 0 20px", color:C.gray400, fontSize:13 }}>Enter your email and we'll send a reset link.</p><form onSubmit={handleReset}><Input label="Email" type="email" placeholder="you@email.com" value={email} onChange={e=>setEmail(e.target.value)} error={error} /><Btn type="submit" variant="primary" size="lg" style={{ width:"100%", justifyContent:"center" }} disabled={loading}>{loading?"Sending…":"Send reset link"}</Btn></form><div style={{ textAlign:"center", marginTop:16 }}><button onClick={()=>setMode("login")} style={{ border:"none", background:"none", color:C.purple, fontWeight:600, cursor:"pointer", fontSize:13 }}>Back to log in</button></div></>
  );

  if (mode==="register") return wrap(
    <><h2 style={{ margin:"0 0 6px", fontSize:19, fontWeight:700 }}>Create your family account</h2><p style={{ margin:"0 0 20px", color:C.gray400, fontSize:13 }}>You'll be the first parent. Add kids after setup.</p><form onSubmit={handleRegister}><Input label="Your name" placeholder="e.g. Sarah" value={name} onChange={e=>setName(e.target.value)} /><Input label="Family name" placeholder="e.g. The Johnson Family" value={familyName} onChange={e=>setFamilyName(e.target.value)} /><Input label="Email" type="email" placeholder="you@email.com" value={email} onChange={e=>setEmail(e.target.value)} /><Input label="Password (min 6 characters)" type="password" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} error={error} /><Btn type="submit" variant="primary" size="lg" style={{ width:"100%", justifyContent:"center", marginTop:4 }} disabled={loading}>{loading?"Creating…":"🎉 Create family account"}</Btn></form><div style={{ textAlign:"center", marginTop:16 }}><button onClick={()=>{ setMode("login"); resetFields(); }} style={{ border:"none", background:"none", color:C.purple, fontWeight:600, cursor:"pointer", fontSize:13 }}>Already have an account? Log in</button></div></>
  );

  if (mode==="join") return wrap(
    <><h2 style={{ margin:"0 0 6px", fontSize:19, fontWeight:700 }}>🎉 Join your family</h2><p style={{ margin:"0 0 20px", color:C.gray400, fontSize:13 }}>Enter your email, the temporary password your parent gave you, then choose your own password.</p><form onSubmit={handleJoin}><Input label="Your email" type="email" placeholder="you@email.com" value={email} onChange={e=>setEmail(e.target.value)} /><Input label="Temporary password (from parent)" type="text" placeholder="The password your parent set" value={password} onChange={e=>setPassword(e.target.value)} /><Input label="Your new password (min 6 chars)" type="password" placeholder="Choose something memorable" value={newPassword} onChange={e=>setNewPassword(e.target.value)} error={error} /><Btn type="submit" variant="primary" size="lg" style={{ width:"100%", justifyContent:"center", marginTop:4 }} disabled={loading||!email||!password||!newPassword}>{loading?"Joining…":"Join family →"}</Btn></form><div style={{ textAlign:"center", marginTop:16 }}><button onClick={()=>{ setMode("login"); resetFields(); }} style={{ border:"none", background:"none", color:C.purple, fontWeight:600, cursor:"pointer", fontSize:13 }}>Back to log in</button></div></>
  );

  return wrap(
    <><h2 style={{ margin:"0 0 20px", fontSize:19, fontWeight:700 }}>Welcome back 👋</h2><form onSubmit={handleLogin}><Input label="Email" type="email" placeholder="you@email.com" value={email} onChange={e=>setEmail(e.target.value)} /><Input label="Password" type="password" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} error={error} /><Btn type="submit" variant="primary" size="lg" style={{ width:"100%", justifyContent:"center", marginTop:4 }} disabled={loading}>{loading?"Logging in…":"Log in"}</Btn></form><div style={{ display:"flex", justifyContent:"space-between", marginTop:16 }}><button onClick={()=>{ setMode("register"); resetFields(); }} style={{ border:"none", background:"none", color:C.purple, fontWeight:600, cursor:"pointer", fontSize:13 }}>Create family account</button><button onClick={()=>{ setMode("reset"); resetFields(); }} style={{ border:"none", background:"none", color:C.gray400, cursor:"pointer", fontSize:13 }}>Forgot password?</button></div><div style={{ borderTop:`1px solid ${C.gray200}`, marginTop:16, paddingTop:16, textAlign:"center" }}><button onClick={()=>{ setMode("join"); resetFields(); }} style={{ border:"none", background:C.purpleLight, color:C.purple, fontWeight:700, cursor:"pointer", fontSize:13, padding:"10px 20px", borderRadius:12, width:"100%" }}>🎉 Join your family (new members click here)</button></div></>
  );
}

// ─── Family Login Picker ──────────────────────────────────────────────────────
function FamilyLoginPicker({ familyMembers, currentAuthUser, onContinueAsParent, onSelectChild }) {
  const self = familyMembers.find(m=>m.uid===currentAuthUser.uid);
  const children = familyMembers.filter(m=>m.role==="child");
  return (
    <div style={{ minHeight:"100vh", background:`linear-gradient(135deg, ${C.purpleLight} 0%, ${C.pinkLight} 50%, ${C.yellowLight} 100%)`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:20, fontFamily:"system-ui, sans-serif" }}>
      <div style={{ fontSize:52, marginBottom:4 }}>🏠✨</div>
      <h1 style={{ fontSize:28, fontWeight:800, color:C.purple, margin:"0 0 4px", letterSpacing:-1 }}>ChoreChart</h1>
      <p style={{ color:C.gray600, marginBottom:32, fontSize:15, marginTop:4 }}>Who's using the app?</p>
      <div style={{ width:"100%", maxWidth:400, display:"flex", flexDirection:"column", gap:10 }}>
        {self && <button onClick={onContinueAsParent} style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 20px", background:C.white, borderRadius:18, border:`2px solid ${C.purpleMid}`, cursor:"pointer", textAlign:"left", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}><Avatar name={self.name} color={CHILD_COLORS[0]} size={44}/><div><div style={{ fontWeight:700, fontSize:16, color:C.gray800 }}>{self.name}</div><div style={{ fontSize:13, color:C.gray400 }}>👑 Parent</div></div></button>}
        {children.map((ch,i) => { const cc=CHILD_COLORS[ch.colorIdx??i%CHILD_COLORS.length]; return <button key={ch.id} onClick={()=>onSelectChild(ch)} style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 20px", background:C.white, borderRadius:18, border:`2px solid ${cc.mid}`, cursor:"pointer", textAlign:"left", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}><Avatar name={ch.name} color={cc} size={44}/><div><div style={{ fontWeight:700, fontSize:16, color:C.gray800 }}>{ch.name}</div><div style={{ fontSize:13, color:C.gray400 }}>⭐ Child{!ch.hasDevice?" · no device":""}</div></div></button>; })}
        <button onClick={()=>signOut(auth)} style={{ marginTop:8, border:"none", background:"none", color:C.gray400, cursor:"pointer", fontSize:13, fontWeight:600 }}>Log out</button>
      </div>
    </div>
  );
}

// ─── Parent App Shell ─────────────────────────────────────────────────────────
function ParentApp({ authUser, profile, family, members, chores, assignments, transactions, onLogout, onSwitchToChild }) {
  const [tab, setTab] = useState("library");
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg,type)=>setToast({msg,type}),[]);
  const tabs = [
    {id:"library",  icon:"📚", label:"Library" },
    {id:"calendar", icon:"📅", label:"Calendar"},
    {id:"verify",   icon:"✅", label:"Verify"  },
    {id:"accounts", icon:"💰", label:"Accounts"},
    {id:"family",   icon:"👨‍👩‍👧‍👦", label:"Family"  },
  ];
  return (
    <div style={{ fontFamily:"system-ui, sans-serif", background:C.offWhite, minHeight:"100vh" }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={()=>setToast(null)} />}
      <div style={{ background:`linear-gradient(135deg, ${C.purple} 0%, ${C.pink} 100%)`, padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", color:"#fff", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}><span style={{ fontSize:24 }}>🏠✨</span><div><div style={{ fontWeight:800, fontSize:17 }}>ChoreChart</div><div style={{ fontSize:12, opacity:0.85 }}>👑 {profile?.name}</div></div></div>
        <div style={{ display:"flex", gap:8 }}>
          {onSwitchToChild && <Btn variant="ghost" size="sm" onClick={onSwitchToChild} style={{ color:"#fff", opacity:0.85 }}>Switch user</Btn>}
          <Btn variant="ghost" size="sm" onClick={onLogout} style={{ color:"#fff", opacity:0.85 }}>Log out</Btn>
        </div>
      </div>
      <div style={{ background:C.white, borderBottom:`1px solid ${C.gray200}`, display:"flex", overflowX:"auto", position:"sticky", top:64, zIndex:99 }}>
        {tabs.map(t=><button key={t.id} onClick={()=>setTab(t.id)} style={{ flex:1, minWidth:60, padding:"12px 6px", border:"none", background:"none", fontSize:11, fontWeight:700, cursor:"pointer", color:tab===t.id?C.purple:C.gray400, borderBottom:`3px solid ${tab===t.id?C.purple:"transparent"}`, transition:"all 0.15s" }}><div style={{ fontSize:18, marginBottom:2 }}>{t.icon}</div>{t.label}</button>)}
      </div>
      <div style={{ padding:"20px 16px", maxWidth:800, margin:"0 auto" }}>
        {tab==="library"  && <ParentLibrary  family={family} members={members} chores={chores} toast={showToast} />}
        {tab==="calendar" && <ParentCalendar family={family} members={members} chores={chores} assignments={assignments} toast={showToast} />}
        {tab==="verify"   && <ParentVerify   family={family} members={members} chores={chores} assignments={assignments} transactions={transactions} toast={showToast} />}
        {tab==="accounts" && <ParentAccounts family={family} members={members} transactions={transactions} toast={showToast} />}
        {tab==="family"   && <ParentFamily   family={family} members={members} transactions={transactions} toast={showToast} />}
      </div>
    </div>
  );
}

// ─── Parent: Library Tab ──────────────────────────────────────────────────────
function ParentLibrary({ family, members, chores, toast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editChore, setEditChore] = useState(null);
  const [name, setName] = useState(""); const [value, setValue] = useState("");
  const [notes, setNotes] = useState(""); const [freq, setFreq] = useState("daily");
  const [schedule, setSchedule] = useState({ startDate:todayStr(), endDate:null, weekDays:[], monthDay:1 });
  const [assignedTo, setAssignedTo] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filterFreq, setFilterFreq] = useState("all");
  const today = todayStr();
  const children = members.filter(m=>m.role==="child");

  function toggleChild(id) { setAssignedTo(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]); }

  function openEdit(ch) {
    setEditChore(ch); setName(ch.name||""); setValue(String(ch.value||""));
    setNotes(ch.notes||""); setFreq(ch.freq||"daily");
    setSchedule({ startDate:ch.startDate||today, endDate:ch.endDate||null, weekDays:ch.weekDays||[], monthDay:ch.monthDay||1 });
    setAssignedTo(ch.assignedTo||[]);
  }

  function closeModal() {
    setShowAdd(false); setEditChore(null); setName(""); setValue(""); setNotes("");
    setFreq("daily"); setSchedule({startDate:today,endDate:null,weekDays:[],monthDay:1}); setAssignedTo([]);
  }

  async function saveChore() {
    if (!name.trim()) return;
    if (freq==="weekly"&&(!schedule.weekDays||schedule.weekDays.length===0)) { toast("Please pick at least one day of the week.","error"); return; }
    setSaving(true);
    const data = {
      name:name.trim(), value:parseFloat(value)||0, notes:notes.trim(), freq,
      startDate:schedule.startDate||today, endDate:schedule.endDate||null,
      weekDays:freq==="weekly"?(schedule.weekDays||[]):[],
      monthDay:freq==="monthly"?(schedule.monthDay||1):null,
      assignedTo,
    };
    try {
      if (editChore) {
        await updateDoc(doc(db,"families",family.id,"chores",editChore.id), data);
        toast("✅ Chore updated!");
      } else {
        await addDoc(collection(db,"families",family.id,"chores"), {...data, createdAt:new Date().toISOString()});
        toast("✅ Chore added!");
      }
      closeModal();
    } catch(err) { toast("Error: "+err.message,"error"); }
    setSaving(false);
  }

  async function deleteChore(id) {
    if (!window.confirm("Remove this chore?")) return;
    try { await deleteDoc(doc(db,"families",family.id,"chores",id)); toast("Chore removed"); }
    catch(err) { toast("Error: "+err.message,"error"); }
  }

  function scheduleLabel(ch) {
    const end = ch.endDate?` → ${ch.endDate}`:" (ongoing)";
    if (ch.freq==="daily") return `Daily from ${ch.startDate}${end}`;
    if (ch.freq==="weekly") return `Every ${(ch.weekDays||[]).map(d=>DAY_SHORT[d]).join(", ")} from ${ch.startDate}${end}`;
    if (ch.freq==="monthly") return `Monthly on the ${ch.monthDay}${ordinal(ch.monthDay)} from ${ch.startDate}${end}`;
    return "";
  }

  const filtered = filterFreq==="all" ? chores : chores.filter(c=>c.freq===filterFreq);
  const isEditing = !!editChore;

  return (
    <>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:800, color:C.gray800 }}>📚 Chore Library</h2>
        <Btn variant="primary" onClick={()=>setShowAdd(true)}>+ Add chore</Btn>
      </div>

      {/* Filter pills */}
      <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
        {[{id:"all",label:"All chores",icon:"📋"},{id:"daily",label:"Daily",icon:"☀️"},{id:"weekly",label:"Weekly",icon:"📅"},{id:"monthly",label:"Monthly",icon:"🗓️"}].map(f=>(
          <button key={f.id} onClick={()=>setFilterFreq(f.id)} style={{ padding:"6px 14px", borderRadius:20, border:`2px solid ${filterFreq===f.id?C.purple:C.gray200}`, background:filterFreq===f.id?C.purpleLight:C.white, color:filterFreq===f.id?C.purple:C.gray600, fontWeight:700, fontSize:13, cursor:"pointer" }}>
            {f.icon} {f.label}
          </button>
        ))}
      </div>

      {filtered.length===0 ? (
        <Card style={{ textAlign:"center", padding:40 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
          <div style={{ color:C.gray400, fontWeight:600 }}>No chores yet. Add one!</div>
        </Card>
      ) : filtered.map(ch=>{
        const assignedNames=(ch.assignedTo||[]).map(id=>members.find(m=>m.id===id)?.name).filter(Boolean);
        const activeToday=choreAppliesToDate(ch,today);
        return (
          <Card key={ch.id} style={{ marginBottom:10, padding:"14px 18px" }}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
              <div style={{ width:44, height:44, borderRadius:14, background:FREQ_CONFIG[ch.freq].light, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>
                {ch.freq==="daily"?"☀️":ch.freq==="weekly"?"📅":"🗓️"}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:15, color:C.gray800 }}>{ch.name}</div>
                {ch.notes&&<div style={{ fontSize:12, color:C.gray400, marginTop:2 }}>{ch.notes}</div>}
                <div style={{ fontSize:12, color:C.gray600, marginTop:4 }}>{scheduleLabel(ch)}</div>
                {assignedNames.length>0 && <div style={{ fontSize:12, color:C.purple, marginTop:2 }}>👤 {assignedNames.join(", ")}</div>}
                <div style={{ marginTop:6, display:"flex", gap:6, flexWrap:"wrap" }}>
                  <span style={{ background:FREQ_CONFIG[ch.freq].light, color:FREQ_CONFIG[ch.freq].color, fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20 }}>{FREQ_CONFIG[ch.freq].icon} {FREQ_CONFIG[ch.freq].label}</span>
                  {activeToday&&<span style={{ background:C.greenLight, color:C.green, fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20 }}>✓ Active today</span>}
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                <div style={{ fontWeight:800, fontSize:18, color:C.green }}>{fmt$(ch.value)}</div>
                <button onClick={()=>openEdit(ch)} style={{ border:"none", background:C.purpleLight, color:C.purple, borderRadius:8, padding:"4px 12px", cursor:"pointer", fontSize:12, fontWeight:600 }}>✏️ Edit</button>
                <button onClick={()=>deleteChore(ch.id)} style={{ border:"none", background:C.redLight, color:C.red, borderRadius:8, padding:"4px 12px", cursor:"pointer", fontSize:12, fontWeight:600 }}>Remove</button>
              </div>
            </div>
          </Card>
        );
      })}

      {/* Add / Edit Modal */}
      {(showAdd||isEditing) && (
        <Modal title={isEditing?`✏️ Edit: ${editChore.name}`:"➕ Add a chore"} onClose={closeModal} wide>
          <Input label="Chore name" placeholder="e.g. Wash the dishes" value={name} onChange={e=>setName(e.target.value)} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <Input label="Value ($)" type="number" min="0" step="0.25" placeholder="1.00" value={value} onChange={e=>setValue(e.target.value)} />
            <Select label="Frequency" value={freq} onChange={e=>{ setFreq(e.target.value); setSchedule({startDate:today,endDate:null,weekDays:[],monthDay:1}); }}>
              <option value="daily">☀️ Daily</option>
              <option value="weekly">📅 Weekly</option>
              <option value="monthly">🗓️ Monthly</option>
            </Select>
          </div>
          <div style={{ background:C.gray50, borderRadius:14, padding:"14px 16px", marginBottom:12, border:`1.5px solid ${C.gray200}` }}>
            <div style={{ fontSize:12, fontWeight:600, color:C.gray600, marginBottom:10 }}>
              {freq==="daily"?"☀️ Schedule":freq==="weekly"?"📅 Which day(s)?":"🗓️ Which day of the month?"}
            </div>
            <SchedulePicker freq={freq} value={schedule} onChange={setSchedule} />
          </div>
          <Input label="Notes (optional)" placeholder="Extra instructions" value={notes} onChange={e=>setNotes(e.target.value)} />
          {children.length>0&&(
            <div style={{ marginBottom:12 }}>
              <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.gray600, marginBottom:8 }}>Assign to</label>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {children.map((ch,i)=>{ const cc=CHILD_COLORS[ch.colorIdx??i%CHILD_COLORS.length]; const sel=assignedTo.includes(ch.id); return <button key={ch.id} type="button" onClick={()=>toggleChild(ch.id)} style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:20, border:`2px solid ${sel?cc.bg:C.gray200}`, background:sel?cc.light:C.gray50, color:sel?cc.bg:C.gray600, fontWeight:700, fontSize:13, cursor:"pointer" }}><Avatar name={ch.name} color={cc} size={20}/>{ch.name}</button>; })}
              </div>
            </div>
          )}
          <div style={{ display:"flex", gap:10, marginTop:4 }}>
            <Btn variant="ghost" onClick={closeModal}>Cancel</Btn>
            <Btn variant="primary" style={{ flex:1, justifyContent:"center" }} onClick={saveChore} disabled={!name.trim()||saving}>
              {saving?"Saving…":isEditing?"Save changes":"Add chore"}
            </Btn>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── Parent: Calendar Tab ─────────────────────────────────────────────────────
function ParentCalendar({ family, members, chores, assignments, toast }) {
  const [view, setView] = useState("daily");
  const [activeChild, setActiveChild] = useState(()=>members.find(m=>m.role==="child")?.id||"");
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const today = todayStr();

  const dates = view==="weekly"?getWeekDates():view==="monthly"?getMonthDates():[selectedDate];

  function getChoresForDate(date) {
    return chores
      .filter(ch=>choreAppliesToDate(ch,date)&&(ch.assignedTo||[]).includes(activeChild))
      .map(ch=>({ chore:ch, assignment:assignments.find(a=>a.choreId===ch.id&&a.childId===activeChild&&a.date===date)||null }));
  }

  const totalAssigned = dates.flatMap(d=>getChoresForDate(d)).length;
  const totalVerified = dates.flatMap(d=>getChoresForDate(d)).filter(i=>i.assignment?.status==="verified").length;

  return (
    <>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ margin:"0 0 16px", fontSize:22, fontWeight:800, color:C.gray800 }}>📅 Calendar</h2>
        <ViewToggle view={view} onChange={v=>{ setView(v); setSelectedDate(today); }} />
      </div>

      <ChildSelector members={members} activeChild={activeChild} onSelect={setActiveChild} />

      {/* Stats strip */}
      {totalAssigned>0 && (
        <div style={{ display:"flex", gap:10, marginBottom:16 }}>
          <div style={{ flex:1, background:C.purpleLight, borderRadius:12, padding:"10px 14px", textAlign:"center" }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.purple }}>ASSIGNED</div>
            <div style={{ fontSize:20, fontWeight:800, color:C.purple }}>{totalAssigned}</div>
          </div>
          <div style={{ flex:1, background:C.greenLight, borderRadius:12, padding:"10px 14px", textAlign:"center" }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.green }}>VERIFIED</div>
            <div style={{ fontSize:20, fontWeight:800, color:C.green }}>{totalVerified}</div>
          </div>
          <div style={{ flex:1, background:C.yellowLight, borderRadius:12, padding:"10px 14px", textAlign:"center" }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.orange }}>REMAINING</div>
            <div style={{ fontSize:20, fontWeight:800, color:C.orange }}>{totalAssigned-totalVerified}</div>
          </div>
        </div>
      )}

      {/* Daily: date picker */}
      {view==="daily" && (
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16, flexWrap:"wrap" }}>
          <Input label="Date" type="date" value={selectedDate} onChange={e=>setSelectedDate(e.target.value)} style={{ maxWidth:180, marginBottom:0 }} />
          {selectedDate!==today && <div style={{ marginTop:18 }}><Btn size="sm" variant="ghost" onClick={()=>setSelectedDate(today)}>Back to today</Btn></div>}
        </div>
      )}

      {/* Daily view — list */}
      {view==="daily" && (()=>{
        const items = getChoresForDate(selectedDate);
        if (!items.length) return <Card style={{ textAlign:"center", padding:40 }}><div style={{ fontSize:48, marginBottom:12 }}>🎉</div><div style={{ color:C.gray400, fontWeight:600 }}>No chores assigned for this day.</div></Card>;
        return items.map(({chore:ch, assignment:a})=>{
          const verified=a?.status==="verified"; const done=a?.status==="done";
          const sc=verified?{label:"Verified ✓",bg:C.greenLight,color:C.green}:done?{label:"Done ⏳",bg:C.yellowLight,color:C.orange}:{label:"Pending",bg:C.gray100,color:C.gray600};
          return (
            <Card key={ch.id} style={{ marginBottom:10, padding:"14px 18px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:12, background:FREQ_CONFIG[ch.freq].light, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>
                  {ch.freq==="daily"?"☀️":ch.freq==="weekly"?"📅":"🗓️"}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:C.gray800 }}>{ch.name}</div>
                  {ch.notes&&<div style={{ fontSize:12, color:C.gray400 }}>{ch.notes}</div>}
                </div>
                <div style={{ fontWeight:800, fontSize:16, color:C.green, marginRight:8 }}>{fmt$(ch.value)}</div>
                <span style={{ background:sc.bg, color:sc.color, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>{sc.label}</span>
              </div>
            </Card>
          );
        });
      })()}

      {/* Weekly view */}
      {view==="weekly" && (
        <WeeklyCalendar getChoresForDate={getChoresForDate} today={today} accentColor={C.purple} onMarkDone={()=>{}} canMark={false} />
      )}

      {/* Monthly view */}
      {view==="monthly" && (
        <Card><MonthlyCalendar getChoresForDate={getChoresForDate} today={today} accentColor={C.purple} /></Card>
      )}
    </>
  );
}

// ─── Parent: Verify ───────────────────────────────────────────────────────────
function ParentVerify({ family, members, chores, assignments, transactions, toast }) {
  const [view, setView] = useState("daily");
  const [activeChild, setActiveChild] = useState(()=>members.find(m=>m.role==="child")?.id||"");
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const today = todayStr();

  const activeChildObj = members.find(m=>m.id===activeChild);
  const noDevice = activeChildObj && !activeChildObj.hasDevice;

  // For daily view use selectedDate (can be past), for weekly/monthly use range
  const dates = view==="daily"?[selectedDate]:view==="weekly"?getWeekDates():getMonthDates();

  function getEffective(date) {
    return chores
      .filter(ch=>choreAppliesToDate(ch,date)&&(ch.assignedTo||[]).includes(activeChild))
      .map(ch=>{ const existing=assignments.find(a=>a.choreId===ch.id&&a.childId===activeChild&&a.date===date); return existing||{id:`auto-${ch.id}-${date}`,choreId:ch.id,childId:activeChild,date,status:"pending",auto:true}; });
  }

  async function verify(a, ch) {
    try {
      if (a.auto) {
        const ref = await addDoc(collection(db,"families",family.id,"assignments"), { choreId:ch.id, childId:activeChild, date:a.date, status:"verified", createdAt:new Date().toISOString() });
        await addDoc(collection(db,"families",family.id,"transactions"), { childId:activeChild, type:"chore_credit", amount:ch.value, description:`Completed: ${ch.name}`, date:a.date, assignmentId:ref.id });
      } else {
        await updateDoc(doc(db,"families",family.id,"assignments",a.id),{status:"verified"});
        await addDoc(collection(db,"families",family.id,"transactions"), { childId:activeChild, type:"chore_credit", amount:ch.value, description:`Completed: ${ch.name}`, date:a.date, assignmentId:a.id });
      }
      toast(`✅ Verified! ${fmt$(ch.value)} added to account`);
    } catch(err) { toast("Error: "+err.message,"error"); }
  }

  async function markAndVerify(a, ch) {
    try {
      const ref = await addDoc(collection(db,"families",family.id,"assignments"), { choreId:ch.id, childId:activeChild, date:a.date, status:"verified", createdAt:new Date().toISOString() });
      await addDoc(collection(db,"families",family.id,"transactions"), { childId:activeChild, type:"chore_credit", amount:ch.value, description:`Completed: ${ch.name}`, date:a.date, assignmentId:ref.id });
      toast(`✅ ${fmt$(ch.value)} added to ${activeChildObj?.name}'s account!`);
    } catch(err) { toast("Error: "+err.message,"error"); }
  }

  async function reject(a) {
    if (a.auto) return;
    try { await updateDoc(doc(db,"families",family.id,"assignments",a.id),{status:"pending"}); toast("Chore sent back to pending"); }
    catch(err) { toast("Error: "+err.message,"error"); }
  }

  const pendingReview = dates.flatMap(d=>getEffective(d).filter(a=>a.status==="done"));
  const highlightCounts = {};
  members.filter(m=>m.role==="child").forEach(ch=>{
    highlightCounts[ch.id]=dates.flatMap(d=>chores.filter(c=>choreAppliesToDate(c,d)&&(c.assignedTo||[]).includes(ch.id)).map(c=>assignments.find(a=>a.choreId===c.id&&a.childId===ch.id&&a.date===d)||{status:"pending"})).filter(a=>a.status==="done").length;
  });

  return (
    <>
      <div style={{ marginBottom:16 }}>
        <h2 style={{ margin:"0 0 16px", fontSize:22, fontWeight:800, color:C.gray800 }}>✅ Verify Chores</h2>
        <ViewToggle view={view} onChange={setView} />
      </div>
      <ChildSelector members={members} activeChild={activeChild} onSelect={setActiveChild} highlightCounts={highlightCounts} />

      {/* Date picker for daily view — allows going back to past dates */}
      {view==="daily" && (
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
          <Input label="Date" type="date" value={selectedDate} onChange={e=>setSelectedDate(e.target.value)} style={{ maxWidth:180, marginBottom:0 }} />
          {selectedDate!==today && (
            <div style={{ marginTop:18 }}>
              <Btn size="sm" variant="ghost" onClick={()=>setSelectedDate(today)}>Back to today</Btn>
            </div>
          )}
          {selectedDate<today && (
            <div style={{ background:C.blueLight, borderRadius:10, padding:"6px 12px", fontSize:12, color:C.blue, fontWeight:600, marginTop:18 }}>
              📅 Viewing past date — you can still mark & verify chores
            </div>
          )}
        </div>
      )}

      {/* No-device notice */}
      {noDevice && (
        <div style={{ background:C.orangeLight, borderRadius:14, padding:"12px 16px", marginBottom:16, border:`1.5px solid ${C.orangeMid}`, fontSize:13, color:C.orange, fontWeight:600 }}>
          📵 {activeChildObj.name} doesn't have a device — use <strong>"Mark & Verify"</strong> to complete chores on their behalf.
        </div>
      )}

      {/* Needs review (device kids only) */}
      {pendingReview.length>0&&!noDevice&&(
        <Card style={{ background:`linear-gradient(135deg, ${C.yellowLight}, ${C.orangeLight})`, border:`1.5px solid ${C.orangeMid}`, marginBottom:16 }}>
          <div style={{ fontWeight:700, color:C.orange, marginBottom:12, fontSize:15 }}>🔔 Needs your review ({pendingReview.length})</div>
          {pendingReview.map(a=>{ const ch=chores.find(c=>c.id===a.choreId); if (!ch) return null; return (
            <div key={a.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${C.orangeMid}` }}>
              <div style={{ flex:1 }}><div style={{ fontWeight:700, fontSize:14, color:C.gray800 }}>{ch.name}</div><div style={{ fontSize:12, color:C.gray600 }}>{a.date}</div></div>
              <div style={{ fontWeight:700, color:C.green, marginRight:8 }}>{fmt$(ch.value)}</div>
              <Btn size="sm" variant="success" onClick={()=>verify(a,ch)}>✓ Verify</Btn>
              <Btn size="sm" variant="danger" onClick={()=>reject(a)}>Reject</Btn>
            </div>
          ); })}
        </Card>
      )}

      {dates.map(date=>{
        const dayA = getEffective(date);
        if (!dayA.length) return null;
        const dt = new Date(date+"T00:00:00");
        const isToday = date===today;
        const isPast = date<today;
        const label = view==="daily"
          ? (isToday?"Today":isPast?`Past — ${MONTH_NAMES[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`:`${MONTH_NAMES[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`)
          : `${DAY_SHORT[dt.getDay()]}, ${MONTH_NAMES[dt.getMonth()]} ${dt.getDate()}`;
        return (
          <Card key={date} style={{ marginBottom:10 }}>
            <div style={{ fontWeight:700, color:isToday?C.purple:isPast?C.orange:C.gray600, fontSize:13, marginBottom:10 }}>
              {label}
              {isToday&&<span style={{ marginLeft:6, background:C.purple, color:"#fff", padding:"1px 8px", borderRadius:10, fontSize:11 }}>Today</span>}
              {isPast&&view!=="daily"&&<span style={{ marginLeft:6, background:C.orangeLight, color:C.orange, padding:"1px 8px", borderRadius:10, fontSize:11 }}>Past</span>}
            </div>
            {dayA.map(a=>{ const ch=chores.find(c=>c.id===a.choreId); if (!ch) return null;
              const sc={pending:{label:"Pending",bg:C.gray100,color:C.gray600},done:{label:"Done ⏳",bg:C.yellowLight,color:C.orange},verified:{label:"Verified ✓",bg:C.greenLight,color:C.green}}[a.status]||{label:a.status,bg:C.gray100,color:C.gray600};
              return (
                <div key={a.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:`1px solid ${C.gray100}` }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:14, color:C.gray800 }}>{ch.name}</div>
                  </div>
                  <div style={{ fontWeight:700, color:C.green, fontSize:13 }}>{fmt$(ch.value)}</div>
                  <span style={{ background:sc.bg, color:sc.color, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>{sc.label}</span>
                  {/* No-device OR past date: show Mark & Verify for any pending chore */}
                  {(noDevice||isPast||isToday) && a.status==="pending" && (
                    <Btn size="sm" variant="success" onClick={()=>markAndVerify(a,ch)}>✓ Mark & Verify</Btn>
                  )}
                  {/* Device kids: verify/reject done chores */}
                  {!noDevice && a.status==="done" && !a.auto && (
                    <div style={{ display:"flex", gap:4 }}>
                      <Btn size="sm" variant="success" onClick={()=>verify(a,ch)}>✓ Verify</Btn>
                      <Btn size="sm" variant="danger" onClick={()=>reject(a)}>Reject</Btn>
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        );
      })}
      {dates.flatMap(d=>getEffective(d)).length===0&&(
        <Card style={{ textAlign:"center", padding:40 }}><div style={{ fontSize:48, marginBottom:12 }}>🎉</div><div style={{ color:C.gray400, fontWeight:600 }}>No chores assigned for this period.</div></Card>
      )}
    </>
  );
}

// ─── Parent: Accounts ─────────────────────────────────────────────────────────
function ParentAccounts({ family, members, transactions, toast }) {
  const [activeChild, setActiveChild] = useState(()=>members.find(m=>m.role==="child")?.id||"");
  const [modal, setModal] = useState(null); // null | 'chore_credit' | 'cash_gift' | 'chore_spend' | 'cash_spend'
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const bal = getChildBalance(transactions, activeChild);
  const txs = transactions.filter(t=>t.childId===activeChild).slice().reverse();
  const txLabels = { chore_credit:"Chore earned", chore_spend:"Chore credit spent", cash_gift:"Cash gift", cash_spend:"Cash spent" };
  const modalConfig = {
    chore_credit:{ title:"➕ Add chore credit",   color:C.green,  desc:"Bonus, reward, or manual credit" },
    cash_gift:   { title:"🎁 Add cash",            color:C.blue,   desc:"Gift or cash received" },
    chore_spend: { title:"🛍️ Spend chore credit",  color:C.orange, desc:"What did they buy?" },
    cash_spend:  { title:"💸 Spend cash",           color:C.red,    desc:"What did they spend on?" },
  };

  async function saveTx() {
    const amt = parseFloat(amount);
    if (!amt||amt<=0) return;
    setSaving(true);
    try {
      await addDoc(collection(db,"families",family.id,"transactions"), { childId:activeChild, type:modal, amount:amt, description:desc.trim()||modalConfig[modal]?.desc||"Transaction", date:todayStr(), createdAt:new Date().toISOString() });
      setAmount(""); setDesc(""); setModal(null);
      toast("💰 Transaction recorded!");
    } catch(err) { toast("Error: "+err.message,"error"); }
    setSaving(false);
  }

  return (
    <>
      <h2 style={{ margin:"0 0 16px", fontSize:22, fontWeight:800, color:C.gray800 }}>💰 Accounts</h2>
      <ChildSelector members={members} activeChild={activeChild} onSelect={setActiveChild} />

      {/* Balances */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:16 }}>
        {[
          {label:"Chore credit",value:fmt$(bal.chore),color:C.green, bg:C.greenLight, icon:"⭐"},
          {label:"Cash on hand",value:fmt$(bal.cash), color:C.blue,  bg:C.blueLight,  icon:"💵"},
          {label:"Total",       value:fmt$(bal.chore+bal.cash),color:C.purple,bg:C.purpleLight,icon:"💎"},
        ].map(m=><div key={m.label} style={{ background:m.bg, borderRadius:16, padding:"14px 12px", textAlign:"center" }}><div style={{ fontSize:22, marginBottom:4 }}>{m.icon}</div><div style={{ fontSize:11, fontWeight:700, color:m.color, marginBottom:4 }}>{m.label}</div><div style={{ fontSize:20, fontWeight:800, color:m.color }}>{m.value}</div></div>)}
      </div>

      {/* Quick action buttons */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        <Btn variant="success" onClick={()=>{ setModal("chore_credit"); setDesc(""); setAmount(""); }} style={{ justifyContent:"center", padding:"12px" }}>⭐ Add chore credit</Btn>
        <Btn variant="primary" onClick={()=>{ setModal("cash_gift"); setDesc(""); setAmount(""); }} style={{ justifyContent:"center", padding:"12px" }}>🎁 Add cash</Btn>
        <Btn variant="orange" onClick={()=>{ setModal("chore_spend"); setDesc(""); setAmount(""); }} style={{ justifyContent:"center", padding:"12px" }}>🛍️ Spend chore credit</Btn>
        <Btn variant="danger" onClick={()=>{ setModal("cash_spend"); setDesc(""); setAmount(""); }} style={{ justifyContent:"center", padding:"12px", background:C.redLight, color:C.red }}>💸 Spend cash</Btn>
      </div>

      {/* Transaction history */}
      <Card>
        <div style={{ fontWeight:700, fontSize:16, color:C.gray800, marginBottom:14 }}>Transaction history</div>
        {txs.length===0
          ? <div style={{ textAlign:"center", color:C.gray400, padding:24, fontWeight:600 }}>No transactions yet</div>
          : txs.map(tx=>{ const isPos=tx.type==="chore_credit"||tx.type==="cash_gift"; const icons={chore_credit:"⭐",chore_spend:"🛍️",cash_gift:"🎁",cash_spend:"💸"}; return (
            <div key={tx.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${C.gray100}` }}>
              <div style={{ width:36, height:36, borderRadius:"50%", background:isPos?C.greenLight:C.redLight, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{icons[tx.type]||"💰"}</div>
              <div style={{ flex:1, minWidth:0 }}><div style={{ fontWeight:600, fontSize:14, color:C.gray800 }}>{tx.description}</div><div style={{ fontSize:11, color:C.gray400 }}>{txLabels[tx.type]||tx.type} · {tx.date}</div></div>
              <div style={{ fontWeight:700, fontSize:15, color:isPos?C.green:C.red }}>{isPos?"+":"-"}{fmt$(tx.amount)}</div>
            </div>
          ); })
        }
      </Card>

      {modal && (
        <Modal title={modalConfig[modal]?.title||"Transaction"} onClose={()=>setModal(null)}>
          <div style={{ fontSize:13, color:C.gray400, marginBottom:16 }}>{modalConfig[modal]?.desc}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            <Input label="Amount ($)" type="number" min="0" step="0.01" placeholder="0.00" value={amount} onChange={e=>setAmount(e.target.value)} />
            <Input label="Description" placeholder="e.g. Birthday bonus" value={desc} onChange={e=>setDesc(e.target.value)} />
          </div>
          <div style={{ display:"flex", gap:10, marginTop:4 }}>
            <Btn variant="ghost" onClick={()=>setModal(null)}>Cancel</Btn>
            <Btn variant="primary" style={{ flex:1, justifyContent:"center" }} disabled={!amount||parseFloat(amount)<=0||saving} onClick={saveTx}>{saving?"Saving…":"Save"}</Btn>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── Parent: Family ───────────────────────────────────────────────────────────
function ParentFamily({ family, members, transactions, toast }) {
  const [name, setName] = useState(""); const [email, setEmail] = useState("");
  const [role, setRole] = useState("child"); const [tempPassword, setTempPassword] = useState("");
  const [hasDevice, setHasDevice] = useState(true);
  const [saving, setSaving] = useState(false); const [createdInfo, setCreatedInfo] = useState(null);

  // Edit state
  const [editMember, setEditMember] = useState(null); // the member being edited
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editHasDevice, setEditHasDevice] = useState(true);
  const [editSaving, setEditSaving] = useState(false);

  function openEdit(m) {
    setEditMember(m);
    setEditName(m.name||"");
    setEditEmail(m.email||"");
    setEditHasDevice(m.hasDevice !== false); // default true if not set
  }

  async function saveEdit() {
    if (!editName.trim()) return;
    setEditSaving(true);
    try {
      await updateDoc(doc(db,"users",editMember.id), {
        name: editName.trim(),
        email: editEmail.trim().toLowerCase(),
        hasDevice: editHasDevice,
      });
      toast(`✅ ${editName.trim()} updated!`);
      setEditMember(null);
    } catch(err) { toast("Error: "+err.message,"error"); }
    setEditSaving(false);
  }

  async function addMember() {
    if (!name.trim()||(hasDevice&&(!email.trim()||!tempPassword.trim()))) return;
    setSaving(true);
    try {
      const children = members.filter(m=>m.role==="child");
      await addDoc(collection(db,"pendingMembers"), {
        name:name.trim(), email:email.trim().toLowerCase(), role, familyId:family.id,
        colorIdx:role==="child"?children.length%CHILD_COLORS.length:0,
        tempPassword:tempPassword.trim(), hasDevice,
        createdAt:new Date().toISOString(),
      });
      if (!hasDevice) {
        const placeholderId = `nodevice-${Date.now()}`;
        await setDoc(doc(db,"users",placeholderId), {
          name:name.trim(), email:"", role, familyId:family.id,
          colorIdx:role==="child"?members.filter(m=>m.role==="child").length%CHILD_COLORS.length:0,
          hasDevice:false, createdAt:new Date().toISOString(),
        });
      }
      setCreatedInfo({ name:name.trim(), email:email.trim(), password:tempPassword.trim(), role, hasDevice });
      setName(""); setEmail(""); setTempPassword(""); setRole("child"); setHasDevice(true);
      toast(`✅ ${name.trim()} added!`);
    } catch(err) { toast("Error: "+err.message,"error"); }
    setSaving(false);
  }

  const parents = members.filter(m=>m.role==="parent");
  const children = members.filter(m=>m.role==="child");

  return (
    <>
      <h2 style={{ margin:"0 0 16px", fontSize:22, fontWeight:800, color:C.gray800 }}>👨‍👩‍👧‍👦 Family</h2>
      <Card style={{ background:`linear-gradient(135deg, ${C.purpleLight}, ${C.pinkLight})`, border:`1.5px solid ${C.purpleMid}` }}>
        <div style={{ fontWeight:800, fontSize:18, color:C.purple }}>{family.name}</div>
        <div style={{ fontSize:13, color:C.gray600, marginTop:4 }}>{members.length} members</div>
      </Card>

      <div style={{ background:C.blueLight, borderRadius:14, padding:"12px 16px", marginBottom:16, border:`1.5px solid ${C.blueMid}`, fontSize:13, color:C.blue }}>
        <strong>ℹ️ How joining works:</strong> Members with a device go to <strong>choretrack.netlify.app</strong>, click <strong>"Join your family"</strong> and use the temp password to set up their account. Members without a device are managed by a parent.
      </div>

      {[{label:"👑 Parents",list:parents},{label:"⭐ Children",list:children}].map(group=>(
        <Card key={group.label} style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.gray600, marginBottom:12 }}>{group.label}</div>
          {group.list.length===0
            ? <div style={{ color:C.gray400, fontSize:13 }}>None added yet</div>
            : group.list.map((m,i)=>{ const cc=CHILD_COLORS[m.colorIdx??i%CHILD_COLORS.length]; const bal=getChildBalance(transactions,m.id); return (
              <div key={m.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderBottom:`1px solid ${C.gray100}` }}>
                <Avatar name={m.name} color={cc} size={40}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:C.gray800 }}>{m.name}</div>
                  <div style={{ fontSize:12, color:C.gray400 }}>{m.email||"No email set"}</div>
                  {m.role==="child"&&<div style={{ fontSize:12, marginTop:3 }}><span style={{ color:C.green, fontWeight:700 }}>{fmt$(bal.chore)} chore</span><span style={{ color:C.gray400, margin:"0 6px" }}>·</span><span style={{ color:C.blue, fontWeight:700 }}>{fmt$(bal.cash)} cash</span></div>}
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                  {m.role==="child"&&(
                    <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:10, background:m.hasDevice!==false?C.blueLight:C.orangeLight, color:m.hasDevice!==false?C.blue:C.orange }}>
                      {m.hasDevice!==false?"📱 Has device":"📵 No device"}
                    </span>
                  )}
                  <button onClick={()=>openEdit(m)} style={{ border:"none", background:C.purpleLight, color:C.purple, borderRadius:8, padding:"4px 12px", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                    ✏️ Edit
                  </button>
                </div>
              </div>
            ); })
          }
        </Card>
      ))}

      {createdInfo&&(
        <Card style={{ background:C.greenLight, border:`1.5px solid ${C.greenMid}`, marginBottom:16 }}>
          <div style={{ fontWeight:700, color:C.green, marginBottom:8 }}>✅ {createdInfo.name} added!</div>
          {createdInfo.hasDevice
            ? <><div style={{ fontSize:13, color:C.gray800, marginBottom:8 }}>Share these details so they can join:</div><div style={{ background:C.white, borderRadius:10, padding:"12px 14px", fontSize:13, lineHeight:1.7 }}><div>🌐 <strong>Website:</strong> choretrack.netlify.app</div><div>📧 <strong>Email:</strong> {createdInfo.email}</div><div>🔑 <strong>Temp password:</strong> {createdInfo.password}</div><div style={{ marginTop:6, fontSize:12, color:C.gray600 }}>They click "Join your family" and set their own password.</div></div></>
            : <div style={{ fontSize:13, color:C.gray800 }}>No device — you'll manage their chores from the Verify tab using "Mark &amp; Verify".</div>
          }
          <Btn size="sm" variant="ghost" onClick={()=>setCreatedInfo(null)} style={{ marginTop:10 }}>Dismiss</Btn>
        </Card>
      )}

      <Card>
        <div style={{ fontWeight:700, fontSize:15, color:C.gray800, marginBottom:14 }}>➕ Add a family member</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <Input label="Name" placeholder="First name" value={name} onChange={e=>setName(e.target.value)} />
          <Select label="Role" value={role} onChange={e=>setRole(e.target.value)}>
            <option value="child">⭐ Child</option>
            <option value="parent">👑 Parent</option>
          </Select>
        </div>
        <Checkbox
          label="This child has their own device"
          checked={hasDevice}
          onChange={setHasDevice}
          note={hasDevice?"They'll log in with their own email and password.":"A parent will mark and verify their chores for them."}
        />
        {hasDevice&&(
          <>
            <Input label="Email address" type="email" placeholder="name@email.com" value={email} onChange={e=>setEmail(e.target.value)} />
            <Input label="Temporary password" type="text" placeholder="Something simple to share, e.g. Family123" value={tempPassword} onChange={e=>setTempPassword(e.target.value)} />
          </>
        )}
        <Btn variant="primary" onClick={addMember} disabled={!name.trim()||(hasDevice&&(!email.trim()||!tempPassword.trim()))||saving} style={{ width:"100%", justifyContent:"center" }}>
          {saving?"Saving…":"➕ Add family member"}
        </Btn>
      </Card>

      {/* Edit Member Modal */}
      {editMember&&(
        <Modal title={`✏️ Edit ${editMember.name}`} onClose={()=>setEditMember(null)}>
          <Input label="Name" placeholder="First name" value={editName} onChange={e=>setEditName(e.target.value)} />
          {editMember.role==="child"&&(
            <Checkbox
              label="This child has their own device"
              checked={editHasDevice}
              onChange={setEditHasDevice}
              note={editHasDevice?"They log in with their own email and password.":"A parent marks and verifies their chores for them."}
            />
          )}
          {editHasDevice&&(
            <Input label="Email address" type="email" placeholder="name@email.com" value={editEmail} onChange={e=>setEditEmail(e.target.value)} />
          )}
          {!editHasDevice&&editMember.role==="child"&&(
            <div style={{ background:C.orangeLight, borderRadius:10, padding:"10px 14px", fontSize:13, color:C.orange, fontWeight:600, marginBottom:12 }}>
              📵 No device — chores will be managed by a parent from the Verify tab.
            </div>
          )}
          <div style={{ display:"flex", gap:10, marginTop:4 }}>
            <Btn variant="ghost" onClick={()=>setEditMember(null)}>Cancel</Btn>
            <Btn variant="primary" style={{ flex:1, justifyContent:"center" }} disabled={!editName.trim()||editSaving} onClick={saveEdit}>
              {editSaving?"Saving…":"Save changes"}
            </Btn>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── Child App ────────────────────────────────────────────────────────────────
function ChildApp({ childProfile, family, chores, assignments, transactions, onLogout, onSwitchUser }) {
  const [tab, setTab] = useState("chores");
  const [view, setView] = useState("daily");
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg,type)=>setToast({msg,type}),[]);
  const cc = CHILD_COLORS[childProfile.colorIdx??0];
  const bal = getChildBalance(transactions, childProfile.id);
  const today = todayStr();

  function getChoresForDate(date) {
    return chores
      .filter(ch=>choreAppliesToDate(ch,date)&&(ch.assignedTo||[]).includes(childProfile.id))
      .map(ch=>({ chore:ch, assignment:assignments.find(a=>a.choreId===ch.id&&a.childId===childProfile.id&&a.date===date)||null }));
  }

  const doneToday = getChoresForDate(today).filter(i=>i.assignment?.status==="verified").length;
  const totalToday = getChoresForDate(today).length;

  async function markDone(chore, existingAssignment, date) {
    if (date!==today) return; // only today
    try {
      if (existingAssignment) {
        await updateDoc(doc(db,"families",family.id,"assignments",existingAssignment.id),{status:"done"});
      } else {
        await addDoc(collection(db,"families",family.id,"assignments"),{ choreId:chore.id, childId:childProfile.id, date, status:"done", createdAt:new Date().toISOString() });
      }
      showToast("🎉 Marked as done! Waiting for parent to verify.");
    } catch(err) { showToast("Error: "+err.message,"error"); }
  }

  const todayItems = getChoresForDate(today);

  return (
    <div style={{ fontFamily:"system-ui, sans-serif", background:cc.light, minHeight:"100vh" }}>
      {toast&&<Toast message={toast.msg} type={toast.type} onClose={()=>setToast(null)}/>}
      {/* Header */}
      <div style={{ background:`linear-gradient(135deg, ${cc.bg} 0%, ${C.pink} 100%)`, padding:"20px 20px 28px", color:"#fff" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}><span style={{ fontSize:28 }}>🏠✨</span><div style={{ fontWeight:800, fontSize:18 }}>ChoreChart</div></div>
          <div style={{ display:"flex", gap:8 }}>
            {onSwitchUser&&<Btn variant="ghost" size="sm" onClick={onSwitchUser} style={{ color:"rgba(255,255,255,0.85)" }}>Switch user</Btn>}
            <Btn variant="ghost" size="sm" onClick={onLogout} style={{ color:"rgba(255,255,255,0.85)" }}>Log out</Btn>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <Avatar name={childProfile.name} color={{ bg:"rgba(255,255,255,0.3)" }} size={56}/>
          <div>
            <div style={{ fontSize:24, fontWeight:800 }}>Hey, {childProfile.name}! 👋</div>
            <div style={{ fontSize:13, opacity:0.9, marginTop:2 }}>{doneToday}/{totalToday} chores done today</div>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:18 }}>
          <div style={{ background:"rgba(255,255,255,0.2)", borderRadius:14, padding:"12px 14px" }}><div style={{ fontSize:11, fontWeight:700, opacity:0.85 }}>⭐ CHORE CREDIT</div><div style={{ fontSize:24, fontWeight:800, marginTop:2 }}>{fmt$(bal.chore)}</div></div>
          <div style={{ background:"rgba(255,255,255,0.2)", borderRadius:14, padding:"12px 14px" }}><div style={{ fontSize:11, fontWeight:700, opacity:0.85 }}>💵 CASH ON HAND</div><div style={{ fontSize:24, fontWeight:800, marginTop:2 }}>{fmt$(bal.cash)}</div></div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background:C.white, display:"flex", borderBottom:`1px solid ${C.gray200}` }}>
        {[{id:"chores",icon:"📋",label:"My Chores"},{id:"account",icon:"💰",label:"My Account"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ flex:1, padding:"12px 8px", border:"none", background:"none", fontSize:12, fontWeight:700, cursor:"pointer", color:tab===t.id?cc.bg:C.gray400, borderBottom:`3px solid ${tab===t.id?cc.bg:"transparent"}` }}>
            <div style={{ fontSize:20, marginBottom:2 }}>{t.icon}</div>{t.label}
          </button>
        ))}
      </div>

      <div style={{ padding:"20px 16px", maxWidth:900, margin:"0 auto" }}>
        {tab==="chores" && (
          <>
            <div style={{ marginBottom:16 }}><ViewToggle view={view} onChange={setView} /></div>

            {/* Daily view */}
            {view==="daily" && (
              <>
                {todayItems.length===0
                  ? <div style={{ textAlign:"center", padding:48 }}><div style={{ fontSize:56, marginBottom:12 }}>🎉</div><div style={{ fontWeight:800, fontSize:20, color:C.gray800, marginBottom:8 }}>No chores today!</div><div style={{ color:C.gray400, fontSize:14 }}>Enjoy your free day 😊</div></div>
                  : todayItems.map(({chore:ch,assignment:a})=>{
                    const done=a?.status==="done"||a?.status==="verified";
                    const verified=a?.status==="verified";
                    return (
                      <div key={ch.id} style={{ background:verified?C.greenLight:done?C.yellowLight:C.white, borderRadius:18, padding:"14px 16px", marginBottom:8, border:`2px solid ${verified?C.greenMid:done?C.yellowMid:C.gray200}`, display:"flex", alignItems:"center", gap:12 }}>
                        <button onClick={()=>!done&&markDone(ch,a,today)} style={{ width:36, height:36, borderRadius:"50%", flexShrink:0, border:`2.5px solid ${verified?C.green:done?C.yellow:cc.bg}`, background:verified?C.green:done?C.yellow:"transparent", cursor:done?"default":"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>
                          {verified?"✓":done?"⏳":""}
                        </button>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:15, color:C.gray800, textDecoration:done?"line-through":"none", opacity:done?0.7:1 }}>{ch.name}</div>
                          {ch.notes&&<div style={{ fontSize:12, color:C.gray400, marginTop:1 }}>{ch.notes}</div>}
                          {verified&&<div style={{ fontSize:12, color:C.green, fontWeight:700, marginTop:4 }}>✅ Verified — {fmt$(ch.value)} earned!</div>}
                          {done&&!verified&&<div style={{ fontSize:12, color:C.orange, fontWeight:600, marginTop:4 }}>⏳ Waiting for parent to verify</div>}
                          {!done&&<div style={{ fontSize:12, color:C.gray400, marginTop:4 }}>Tap the circle when done 👆</div>}
                        </div>
                        <div style={{ fontWeight:800, fontSize:17, color:verified?C.green:C.gray400 }}>{fmt$(ch.value)}</div>
                      </div>
                    );
                  })
                }
              </>
            )}

            {/* Weekly view */}
            {view==="weekly" && (
              <>
                <div style={{ fontSize:12, color:C.gray400, marginBottom:12 }}>🔒 = can only mark today's chores as done</div>
                <WeeklyCalendar getChoresForDate={getChoresForDate} today={today} accentColor={cc.bg} onMarkDone={markDone} canMark={true} />
              </>
            )}

            {/* Monthly view */}
            {view==="monthly" && (
              <Card>
                <MonthlyCalendar getChoresForDate={getChoresForDate} today={today} accentColor={cc.bg} />
              </Card>
            )}
          </>
        )}

        {tab==="account" && (
          <>
            <Card style={{ background:`linear-gradient(135deg, ${cc.bg}, ${C.pink})`, color:"#fff", border:"none" }}>
              <div style={{ fontSize:18, fontWeight:800, marginBottom:16 }}>💎 Your total: {fmt$(bal.chore+bal.cash)}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div style={{ background:"rgba(255,255,255,0.2)", borderRadius:12, padding:12 }}><div style={{ fontSize:11, fontWeight:700, opacity:0.85 }}>⭐ CHORE CREDIT</div><div style={{ fontSize:22, fontWeight:800, marginTop:4 }}>{fmt$(bal.chore)}</div></div>
                <div style={{ background:"rgba(255,255,255,0.2)", borderRadius:12, padding:12 }}><div style={{ fontSize:11, fontWeight:700, opacity:0.85 }}>💵 CASH</div><div style={{ fontSize:22, fontWeight:800, marginTop:4 }}>{fmt$(bal.cash)}</div></div>
              </div>
            </Card>
            <div style={{ background:C.yellowLight, borderRadius:16, padding:"12px 16px", marginBottom:16, border:`1.5px solid ${C.yellowMid}`, fontSize:13, color:C.gray600, fontWeight:600 }}>
              💬 Want to spend your credit? Talk to a parent!
            </div>
            <Card>
              <div style={{ fontWeight:700, fontSize:16, color:C.gray800, marginBottom:14 }}>📜 My history</div>
              {transactions.filter(t=>t.childId===childProfile.id).length===0
                ? <div style={{ textAlign:"center", color:C.gray400, padding:24 }}>Nothing yet — start completing chores!</div>
                : transactions.filter(t=>t.childId===childProfile.id).slice().reverse().map(tx=>{ const isPos=tx.type==="chore_credit"||tx.type==="cash_gift"; const icons={chore_credit:"⭐",chore_spend:"🛍️",cash_gift:"🎁",cash_spend:"💸"}; return (
                  <div key={tx.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${C.gray100}` }}>
                    <div style={{ fontSize:24 }}>{icons[tx.type]||"💰"}</div>
                    <div style={{ flex:1 }}><div style={{ fontWeight:600, fontSize:14, color:C.gray800 }}>{tx.description}</div><div style={{ fontSize:11, color:C.gray400 }}>{tx.date}</div></div>
                    <div style={{ fontWeight:700, fontSize:15, color:isPos?C.green:C.red }}>{isPos?"+":"-"}{fmt$(tx.amount)}</div>
                  </div>
                ); })
              }
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [authUser, setAuthUser]         = useState(undefined);
  const [profile, setProfile]           = useState(null);
  const [family, setFamily]             = useState(null);
  const [members, setMembers]           = useState([]);
  const [chores, setChores]             = useState([]);
  const [assignments, setAssignments]   = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [viewingChild, setViewingChild] = useState(null);
  const [pickingUser, setPickingUser]   = useState(false);

  useEffect(()=>{
    return onAuthStateChanged(auth, async user=>{
      setAuthUser(user||null);
      if (!user) { setProfile(null); setFamily(null); setMembers([]); return; }
      const snap = await getDoc(doc(db,"users",user.uid));
      if (snap.exists()) { const p={id:snap.id,uid:snap.id,...snap.data()}; setProfile(p); loadFamily(p.familyId); }
    });
  },[]);

  function loadFamily(familyId) {
    if (!familyId) return;
    onSnapshot(doc(db,"families",familyId), snap=>{ if (snap.exists()) setFamily({id:snap.id,...snap.data()}); else setFamily({id:familyId,name:"My Family"}); });
    onSnapshot(query(collection(db,"users"),where("familyId","==",familyId)), snap=>setMembers(snap.docs.map(d=>({id:d.id,uid:d.id,...d.data()}))));
    onSnapshot(collection(db,"families",familyId,"chores"), snap=>setChores(snap.docs.map(d=>({id:d.id,...d.data()}))));
    onSnapshot(collection(db,"families",familyId,"assignments"), snap=>setAssignments(snap.docs.map(d=>({id:d.id,...d.data()}))));
    onSnapshot(collection(db,"families",familyId,"transactions"), snap=>setTransactions(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }

  function handleLogout() { setViewingChild(null); setPickingUser(false); signOut(auth); }

  if (authUser===undefined) return <Spinner text="Loading ChoreChart…"/>;
  if (!authUser) return <AuthScreen/>;
  if (!profile||!family) return <Spinner text="Loading your family…"/>;

  if (profile.role==="parent"&&pickingUser) return (
    <FamilyLoginPicker familyMembers={members} currentAuthUser={authUser}
      onContinueAsParent={()=>{ setPickingUser(false); setViewingChild(null); }}
      onSelectChild={ch=>{ setViewingChild(ch); setPickingUser(false); }}
    />
  );

  if (profile.role==="parent"&&viewingChild) return (
    <ChildApp childProfile={viewingChild} family={family} chores={chores} assignments={assignments} transactions={transactions} onLogout={handleLogout} onSwitchUser={()=>setPickingUser(true)}/>
  );

  if (profile.role==="child") return (
    <ChildApp childProfile={profile} family={family} chores={chores} assignments={assignments} transactions={transactions} onLogout={handleLogout} onSwitchUser={null}/>
  );

  return (
    <ParentApp authUser={authUser} profile={profile} family={family} members={members} chores={chores} assignments={assignments} transactions={transactions} onLogout={handleLogout}
      onSwitchToChild={members.filter(m=>m.role==="child").length>0?()=>setPickingUser(true):null}
    />
  );
}
